import { defineStore } from "pinia";
import { updateHeaderConfig } from "@/api/net/config.js";
import tools from "@/utils/tools";
import GlobalConfig from "@/config/config.js";
import apiUser from "@/api/api/apiUser.js";
import router from "../router";
import { funComponentList } from "@/components/functionComp/index";
import { getRealityUser, updateRealityUser } from "@/api/api/apiCommon.js";
import {
  showLoadingDialog,
  closeLoadingDialog,
} from "@/components/functionCallComponent/loading/loading.js";

import { closeAllDialog } from "@/components/basic/dialog/dialog.js";
import { closeAllActionDialog } from "@/components/functionCallComponent/action/action.js";
import { $$language } from "@/i18n/i18n.js";
import { firebaseUtils } from "@/utils/analytic/firebaseUtils.js";
import { isDev } from "@/utils/tools.js";

import { getCreditInfo, getSubscriptionStatus } from "@/api/api/apiCredit.js";
import sa from "sa-sdk-javascript";
import message from "@/components/functionCallComponent/message/message.js";
import {
  getLatestUtmSourceFromCookie,
  getLatestUtmSource,
} from "@/api/net/config.js";
import { mapBrowserLanguage } from "@/i18n/i18n.js";
import { inMurekaApp } from "@/utils/tools.js";
import { jsBridge } from "@/utils/jsBridge.js";
import { queryCertification } from "@/api/api/apiKey.js";

const getLocalData = () => {
  const localData =
    localStorage.getItem(GlobalConfig.localStorageGlobalKey) || "{}";
  const {
    userInfo,
    authData,
    config,
    isLogin,
    k_sso_token,
    appLanguage,
    ..._data
  } = JSON.parse(localData);
  if (!isLogin) {
    sa.logout();
    return { appLanguage: mapBrowserLanguage(appLanguage) };
  } else {
    sa.login(authData?.user_id?.toString());
  }
  if (authData) {
    return {
      k_sso_token,
      appLanguage: mapBrowserLanguage(appLanguage),
      userInfo,
      isLogin,
      authData,
      config,
      ..._data,
    };
  }
  return {};
};

const useUserDataStore = defineStore({
  id: "userDataStore",
  state: () => {
    const data = {
      credits: 0,
      skyworkInfo: {
        isApp: true,
        isCheckLogin: false,
      },
      appInfo: {
        name: "Platform.Mureka.Ai",
      },
      isLogin: false, // 是否登陆
      authData: {},
      userInfo: {
        // 全局用户信息
      },
      config: {
        // 全局配置
      },
      appLanguage: "",
      k_sso_token: "",
      userRelData: null,
      bannerList: null,
      activityConfig: null,
      creditInfo: {},
      hasInit: false,
      lastCheckedTime: "",
      showLastCheckModal: false,
      channel: "",
      ...getLocalData(),
      certificationState: -1, // 认证状态
      personal_un_effect: 0, // 个人认证状态
      enterprise_un_effect: 0, // 企业认证状态
      certificationDetail: {}, // 认证详情,
      enterprise_un_effect_within_30_days: 0, // 企业认证状态(30天内)
    };
    data.authData && updateHeaderConfig(data.authData);
    return data;
  },
  actions: {
    async init() {
      try {
        if (inMurekaApp()) {
          await this.applogin();
        }
        const userInfo = await this.getUserInfo();
      } catch (err) {
        throw err; // 如果需要继续向外抛出错误，可以保留此行
      }
    },
    getUserRealData() {
      return new Promise((resolve, reject) => {
        getRealityUser({}).then((res) => {
          if (res.code == 200) {
            this.hasInit = true;
            this.setUserRelData(res.data);
          }
        });
      });
    },
    // 删除cookie的函数
    deleteSessionCookie() {
      console.log("清除成功");
      const cookieName = `skm_session${isDev() ? "_test" : ""}`;
      const domain = window.location.hostname;

      // 两种方式确保删除
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
    },
    clearAll(isClearLocalData = true) {
      localStorage.removeItem(GlobalConfig.localStorageGlobalKey);
      this.authData = {};
      this.userInfo = {};
      this.config = {};
      this.hasInit = false;
      this.clearOther();
      this.updateLoginStatus(false);
      this.deleteSessionCookie();
    },
    clearOther() {
      updateHeaderConfig(this.authData);
    },
    getUserInfo() {
      return new Promise((resolve, reject) => {
        apiUser.userGetInfo().then((res) => {
          const { data, code, msg } = res;
          if (code == 200) {
            this.credits = data.credits;
            this.userInfo = data ?? {};
            if (!this.authData?.user_id) {
              this.authData = {
                user_id: data?.userInfo?.user?.user_id,
              };
            }
            this.updateLoginStatus(true);
            // this.checkSubscriptionStatus()
            resolve(data);
          } else {
            this.clearAll();
            reject({});
          }
        });
      });
    },
    checkPayState() {
      console.log("正在检查支付状态...");
      const { data, code } = getSubscriptionStatus();
      if (code == 200) {
        data.items.forEach((item) => {
          if (item.origin_status == 6) {
            this.showLastCheckModal = true;
            this.channel = item.channel;
          }
        });
      }
      const now = new Date().toLocaleString();
      this.lastCheckedTime = now; // 更新最后一次检查时间
      localStorage.setItem("lastCheckedDate", now); // 保存到 localStorage
    },
    checkSubscriptionStatus() {
      const lastCheckedDate = localStorage.getItem("lastCheckedDate") ?? "";
      const now = new Date();
      const lastCheckedDateObj = lastCheckedDate
        ? new Date(lastCheckedDate)
        : null;

      if (
        !lastCheckedDate ||
        lastCheckedDateObj.toDateString() !== now.toDateString()
      ) {
        this.checkPayState();
      } else {
        this.lastCheckedTime = lastCheckedDate; // 显示上次检查的时间
      }
    },
    async applogin() {
      if (jsBridge.inMurekaApp) {
        const { user_id, token } = await jsBridge("state.session");
        console.log(
          "view.saveFile res",
          user_id,
          token,
          this?.authData?.user_id
        );
        if (user_id && this?.authData?.user_id == user_id) {
          console.log("User is already logged in.");
          return;
        }

        if (token) {
          await this.appLoginState(token);
        } else {
          this.clearAll();
        }
      }
    },
    getAll() {
      const { isLogin, authData, userInfo, config, appLanguage, k_sso_token } =
        this;
      return {
        isLogin,
        authData,
        userInfo,
        config,
        appLanguage,
        k_sso_token,
      };
    },
    setCredits(credits) {
      this.credits = credits;
    },
    setData(data) {
      Object.assign(this, data);
      localStorage.setItem(
        GlobalConfig.localStorageGlobalKey,
        JSON.stringify(this.getAll())
      );
    },
    updateLoginStatus(isLogin) {
      this.isLogin = isLogin;
      localStorage.setItem(
        GlobalConfig.localStorageGlobalKey,
        JSON.stringify(this.getAll())
      );
      if (isLogin) {
        firebaseUtils.logLoginEvent(this?.authData?.user_id ?? 0);
      } else {
        sa.logout();
      }
    },
    updateUserInfo(userInfo) {
      this.userInfo = userInfo;
      localStorage.setItem(
        GlobalConfig.localStorageGlobalKey,
        JSON.stringify(this.getAll())
      );
    },
    upDataAuthData(authData) {
      this.authData = authData;
      updateHeaderConfig(authData);
      localStorage.setItem(
        GlobalConfig.localStorageGlobalKey,
        JSON.stringify(this.getAll())
      );
      this.init();
    },
    getActivitySource() {
      const utm_source = getLatestUtmSource();
      if (utm_source) {
        return "activity_kol_link";
      }
      return localStorage.getItem("activity_code") ?? "";
    },
    inviteCodePoint(invite) {
      apiUser.inviteCodePoint({ code: invite }).then(() => {
        localStorage.removeItem("invite_code");
        localStorage.removeItem("activity_code");
        const newUrl = this.removeURLParameter(
          window.location.href,
          "invite_code"
        );
        window.history.replaceState(null, "", newUrl);
      });
    },
    getSignupSource(signupSource) {
      return "api_platfrom";
      // if (signupSource) {
      //   return signupSource
      // }
      // const signup_source = sessionStorage.getItem("signup_source")??""
      // if (signup_source){
      //   sessionStorage.removeItem("signup_source")
      //   return signup_source
      // }
      // const invite = localStorage.getItem("invite_code") ?? ""
      // if (invite) {
      //   return "invite_activity"
      // }
      // const pathname = window.location.pathname ?? ""
      // if (pathname == "/") {
      //   return "home_basic"
      // } else if (pathname == "/create") {
      //   if (createEditMode.value == "easy") {
      //     return "create_basic"
      //   } else {
      //     return "create_advance"
      //   }
      // } else if (pathname == "/special-christmas-gift" || pathname == "/gift-detail") {
      //   return "activity"
      // } else {
      //   return "other"
      // }
    },
    removeURLParameter(url, parameter) {
      const urlObj = new URL(url);
      urlObj.searchParams.delete(parameter);
      return urlObj.toString();
    },
    point() {
      apiUser.userPoint().then(() => {});
    },
    checkLoginStatus(params = {}, callback) {
      // rightType, create_type
      let loginDialog = null;
      return new Promise((resolve, reject) => {
        if (this.isLogin) {
          resolve("login");
        } else {
          closeAllDialog();
          closeAllActionDialog();
          if (inMurekaApp()) {
            jsBridge("view.login");
            resolve();
            return;
          }
          if (GlobalConfig.appname == "MurekaCn") {
            loginDialog = funComponentList.LogInCn({
              rightType: params?.rightType,
              create_type: params?.create_type,
              source: params?.source,
              handleOk: (str) => {
                callback && callback(this.isLogin);
              },
              handleCancel: () => {
                callback && callback(false);
              },
            });
          } else {
            loginDialog = funComponentList.ThirdLogin({
              rightType: params?.rightType,
              create_type: params?.create_type,
              source: params?.source,
              signup_source: params?.signup_source ?? "",
              handleOk: (str) => {
                callback && callback(this.isLogin);
              },
              handleCancel: () => {
                callback && callback(false);
              },
            });
          }
          resolve(loginDialog);
        }
      });
    },
    setUserRelData(data) {
      this.userRelData = Object.assign({}, this.userRelData ?? {}, data ?? {});
    },
    switchLanguage(curLanguage) {
      if (curLanguage == this.appLanguage) return;
      this.appLanguage = curLanguage;
      localStorage.setItem(
        GlobalConfig.localStorageGlobalKey,
        JSON.stringify(this.getAll())
      );
    },
    getBanerConfig() {
      return new Promise((resolve, reject) => {
        apiUser
          .getBanerConfig({})
          .then((res) => {
            const { data, code, msg } = res;
            if (code == "200") {
              this.bannerList = data.list ?? [];
              this.activityConfig = data.activity_config ?? {};
              resolve();
            } else {
              reject({});
            }
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    refreshUserAccount() {
      return new Promise((resolve, reject) => {
        getCreditInfo()
          .then((res) => {
            if (res.code == 200) {
              const { data } = res;
              this.credits = data?.gold || 0;
              resolve(this.credits);
            } else {
              reject(res);
            }
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    setCreditsInfo(creditsInfo) {
      this.creditInfo = creditsInfo;
      this.credits = creditsInfo?.gold || 0;
    },
    updateAuthData(_data) {
      this.upDataAuthData(_data.auth);
      this.updateLoginStatus(true);
    },
    async threeLogin(
      x_auth_token,
      is_auto = false,
      signup_source,
      mode = "google"
    ) {
      return new Promise(async (resolve, reject) => {
        const { data, code, msg } = await apiUser.userLogin({
          mode: mode ?? "google",
          ...(is_auto ? { extra_token: "auto_login" } : {}),
          token: x_auth_token,
          source: getLatestUtmSourceFromCookie() ?? "",
          signup_source: this.getSignupSource(signup_source),
          activity_source: this.getActivitySource(),
        });
        if (code === 200) {
          console.log("第三方登录成功");
          this.updateAuthData(data);
          // 检查是否从购买触发的注册
          const purchaseTriggerData = sessionStorage.getItem(
            "purchase_trigger_signup"
          );
          // TODD
          if (purchaseTriggerData && data?.is_new) {
            try {
              // 追踪从购买触发到完成注册的转化
              firebaseUtils.logClickEvent("api_purchase_signup_completed");
              // 清除标记
              sessionStorage.removeItem("purchase_trigger_signup");
            } catch (e) {
              console.error("Failed to parse purchase trigger data", e);
            }
          }

          if (data?.is_new) {
            this.invite();
          }
          resolve();
        } else if (code == 6401) {
          message.error({
            maskClosable: true,
            position: "top",
            content: "Deleted accounts cannot be registered within 14 days.",
          });
          resolve();
        } else {
          message.error({
            maskClosable: true,
            position: "top",
            content: msg,
          });
          resolve();
        }
      });
    },
    async appLoginState(x_auth_token) {
      return new Promise(async (resolve, reject) => {
        const { data, code, msg } = await apiUser.userLogin({
          mode: "self",
          token: x_auth_token,
          source: getLatestUtmSourceFromCookie() ?? "",
          signup_source: this.getSignupSource(),
        });
        if (code === 200) {
          console.log("第三方登录成功");
          this.updateAuthData(data);
          resolve();
        } else if (code == 6401) {
          message.error({
            maskClosable: true,
            position: "top",
            content: "Deleted accounts cannot be registered within 14 days.",
          });
          resolve();
        } else {
          message.error({
            maskClosable: true,
            position: "top",
            content: msg,
          });
          resolve();
        }
      });
    },
    invite() {
      const invite = localStorage.getItem("invite_code") ?? "";
      if (invite) {
        this.inviteCodePoint(invite);
      }
    },

    // 查询认证状态
    getUserCertificationState() {
      return new Promise((resolve) => {
        queryCertification().then((res) => {
          console.log("resqueryCertification", res);
          if (res.code == 200) {
            const {
              state,
              personal_un_effect,
              enterprise_un_effect,
              detail,
              enterprise_un_effect_within_30_days,
            } = res.data;
            this.certificationState = state || 0;
            (this.personal_un_effect = personal_un_effect || 0), // 个人认证状态
              (this.enterprise_un_effect = enterprise_un_effect || 0); // 认证状态
            this.certificationDetail = detail || {};
            this.enterprise_un_effect_within_30_days =
              enterprise_un_effect_within_30_days || 0;
            resolve();
          }
        });
      });
    },
  },
  getters: {
    getAuthData() {
      return this.authData;
    },
    getAppInfo() {
      return this.appInfo;
    },
  },
});
export default useUserDataStore;
